<script>
import AddCustomSla from './AddCustomSla.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { displayTime } from 'dashboard/helper/customSlaTimeHelper';

export default {
  components: {
    AddCustomSla,
    SettingsLayout,
    BaseSettingsHeader,
    BaseTable,
    BaseTableRow,
    BaseTableCell,
    WootLabel,
    Icon,
    NextButton,
    ToggleSwitch,
  },
  data() {
    return {
      loading: {},
      togglingActive: {},
      showAddPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      records: 'customSla/getCustomSla',
      uiFlags: 'customSla/getUIFlags',
    }),
    deleteConfirmText() {
      return this.$t('CUSTOM_SLA.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('CUSTOM_SLA.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}`;
    },
    tableHeaders() {
      return [
        this.$t('CUSTOM_SLA.LIST.TABLE_HEADER.SLA'),
        this.$t('CUSTOM_SLA.LIST.TABLE_HEADER.BUSINESS_HOURS'),
        this.$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT'),
        this.$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT'),
        this.$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT'),
        this.$t('CUSTOM_SLA.LIST.TABLE_HEADER.STATUS'),
        this.$t('INTEGRATION_APPS.LIST.ACTIONS'),
      ];
    },
    filteredRecords() {
      const query = this.searchQuery.trim();
      if (!query) return this.records;
      return picoSearch(this.records, query, ['name', 'description']);
    },
  },
  mounted() {
    this.$store.dispatch('customSla/get');
  },
  methods: {
    displayTime,
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteCustomSla(this.selectedResponse.id);
    },
    deleteCustomSla(id) {
      this.$store
        .dispatch('customSla/delete', id)
        .then(() => {
          useAlert(this.$t('CUSTOM_SLA.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          useAlert(this.$t('CUSTOM_SLA.DELETE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[this.selectedResponse.id] = false;
        });
    },
    toggleActive(customSla) {
      this.togglingActive[customSla.id] = true;
      this.$store
        .dispatch('customSla/update', {
          id: customSla.id,
          active: !customSla.active,
        })
        .then(() => {
          useAlert(
            customSla.active
              ? this.$t('CUSTOM_SLA.LIST.DEACTIVATED')
              : this.$t('CUSTOM_SLA.LIST.ACTIVATED')
          );
        })
        .catch(error => {
          useAlert(
            error?.message || this.$t('CUSTOM_SLA.LIST.ACTIVATION_ERROR')
          );
        })
        .finally(() => {
          this.togglingActive[customSla.id] = false;
        });
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CUSTOM_SLA.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('CUSTOM_SLA.HEADER')"
        :description="$t('CUSTOM_SLA.DESCRIPTION')"
        :link-text="$t('CUSTOM_SLA.LEARN_MORE')"
        :search-placeholder="$t('CUSTOM_SLA.SEARCH_PLACEHOLDER')"
        feature-name="custom_sla"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CUSTOM_SLA.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <NextButton
            :label="$t('CUSTOM_SLA.ADD_ACTION')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          !records.length
            ? $t('CUSTOM_SLA.LIST.404')
            : searchQuery && !filteredRecords.length
              ? $t('CUSTOM_SLA.SEARCH.NO_RESULTS')
              : ''
        "
      >
        <template #header-2>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT') }}
            </span>
            <Icon
              v-tooltip.left="$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.FRT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #header-3>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT') }}
            </span>
            <Icon
              v-tooltip.left="$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.NRT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #header-4>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('CUSTOM_SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT') }}
            </span>
            <Icon
              v-tooltip.left="$t('CUSTOM_SLA.LIST.RESPONSE_TYPES.RT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #row="{ items }">
          <BaseTableRow
            v-for="customSla in items"
            :key="customSla.id"
            :item="customSla"
          >
            <template #default>
              <BaseTableCell>
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-body-main text-n-slate-12 truncate">
                    {{ customSla.name }}
                  </span>
                  <span class="text-body-main text-n-slate-11 line-clamp-1">
                    {{ customSla.description }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell class="w-40">
                <WootLabel
                  :label="
                    customSla.only_during_business_hours
                      ? $t('CUSTOM_SLA.LIST.BUSINESS_HOURS_ON')
                      : $t('CUSTOM_SLA.LIST.BUSINESS_HOURS_OFF')
                  "
                  :color="
                    customSla.only_during_business_hours ? 'teal' : 'slate'
                  "
                  compact
                >
                  <template #icon>
                    <Icon
                      :icon="
                        customSla.only_during_business_hours
                          ? 'i-lucide-alarm-clock-check'
                          : 'i-lucide-alarm-clock-off'
                      "
                      class="size-3.5"
                      :class="
                        customSla.only_during_business_hours
                          ? 'text-n-teal-11'
                          : 'text-n-slate-11'
                      "
                    />
                  </template>
                </WootLabel>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(customSla.first_response_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(customSla.next_response_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(customSla.resolution_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-32">
                <div class="flex items-center gap-2">
                  <ToggleSwitch
                    :model-value="customSla.active"
                    :disabled="togglingActive[customSla.id]"
                    @update:model-value="toggleActive(customSla)"
                  />
                  <span
                    class="text-body-main"
                    :class="
                      customSla.active ? 'text-n-teal-11' : 'text-n-slate-11'
                    "
                  >
                    {{
                      customSla.active
                        ? $t('CUSTOM_SLA.LIST.ACTIVE')
                        : $t('CUSTOM_SLA.LIST.INACTIVE')
                    }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-12">
                <div class="flex justify-end">
                  <NextButton
                    v-tooltip.top="$t('CUSTOM_SLA.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[customSla.id]"
                    @click="openDeletePopup(customSla)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>

      <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
        <AddCustomSla @close="hideAddPopup" />
      </woot-modal>

      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('CUSTOM_SLA.DELETE.CONFIRM.TITLE')"
        :message="$t('CUSTOM_SLA.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
      />
    </template>
  </SettingsLayout>
</template>
